import { COUNTRY_CODES } from '@ygtechnologies/bygr-frontend-component-library';
import { FileData } from '@ygtechnologies/rubus-frontend-utilities';

export enum SortDirection {
    Ascending = 'Ascending',
    Descending = 'Descending',
}

export interface IdNamePair {
    id: number;
    name: string;
}

export interface PaginationResult<T> {
    totalCount: number;
    items: T[];
}

export interface PaginationRequest {
    pageSize: number;
    pageNumber: number;
    sortBy: string | number | symbol;
    sortDirection: SortDirection;
}

export interface PaginatedSearchRequest extends PaginationRequest {
    searchText: string;
}

export interface BygrUser {
    id: number;
    email: string;
    phoneNumber: string | null;
    displayName: string;
    lastVisitDate: Date | string | null;
    profileMemberships: BygrUserProfileMembership[];
    isBuyer: boolean;
    isSeller: boolean;
    isPaying: boolean;
    selectedLanguageCode: string;
    isCmAdmin: boolean;
    isAmAdmin: boolean;
    isQmAdmin: boolean;
    registeredDate: Date | string | null;
}

export interface BygrUserDetails extends BygrUser {
    projectMemberships: BygrUserProjectMembership[];
    units: BygrProfileUnits[];
}
export interface BygrProfileUnits {
    unitId: number;
    unitName: string;
    projectId: number;
    projectName: string;
    contractStatus: string;
}

export interface BygrUserProfileMembership {
    profileId: number;
    profileName: string;
    profileType: 'Buyer' | 'Seller';
    isPaying: boolean;
}

export interface BygrUserProjectMembership {
    projectId: number;
    projectName: string;
    profileId: number;
    profileName: string;
    isAdmin: boolean;
    bygrModules: BygrModule;
}

export interface BygrProject {
    id: number;
    parentProjectId?: number;
    isParentProject: boolean;
    name: string;
    numberOfUnits: number;
    numberOfUnitsLast7Days: number;
    sellerProfile: IdNamePair;
    sellerModules: BygrModule;
    createDate: Date;
}

export enum BygrModule {
    None = 0,
    ChangeManagement = 1 << 0,
    Aftermarket = 1 << 1,
    QualityManagement = 1 << 2,
    Sales = 1 << 3,
}

export interface BygrProjectDetails extends BygrProject {
    members: ProjectMember[];
    subProject?: BygrProject;
    // invitations: ProjectInvitations[];
}
export interface ProjectDashboardData {
    projectId: number;
    numberOfUnits: number;
    numberOfSoldUnits: number;
    numberOfBuyers: number;
}
export interface ProjectMember {
    profileId: number;
    profileName: string;
    userId: number | null;
    userName: string;
    email: string;
    bygrModules: BygrModule;
    isCmAdministrator: boolean;
    isAmAdministrator: boolean;
    isQmAdministrator: boolean;

    isOwner: boolean;
    source: ProjectMemberSource;
}
export interface BygrProfile {
    id: number;
    name: string;
    numberOfMembers: number;
    isPaying: boolean;
    analyticsIsEnabled: boolean;
}

export interface ProfileTreeMember {
    profileMemberId: number;
    userId: number;
    userName: string;
    email: string | null;
    isOwner: boolean;
    isAdmin: boolean;
    source: number;
}

export interface ProfileTreeProjectMember {
    projectMemberId: number;
    userId: number;
    userName: string;
    email: string | null;
    isOwner: boolean;
    isAdmin: boolean;
    bygrModules: number;
    source: number;
    // Inferred flags (only meaningful when source === Inherited).
    isInheritedAdmin: boolean;
    hasInheritedAnalytics: boolean;
}

export interface ProfileTreeProject {
    id: number;
    name: string;
    numberOfMembers: number;
    sellerModules: number;
    members: ProfileTreeProjectMember[];
}

export interface ProfileTreeNode {
    id: number;
    name: string;
    isPaying: boolean;
    numberOfMembers: number;
    analyticsEnabled: boolean;
    members: ProfileTreeMember[];
    projects: ProfileTreeProject[];
    children: ProfileTreeNode[];
}

export enum ProfileType {
    Seller = 'Seller',
    Buyer = 'Buyer',
}

export interface BygrProfileDetails extends BygrProfile {
    members: ProfileMember[];
    invitations: ProfileInvite[];
    projects: BygrProject[];
    numberOfUnitsTotal: number;
    createDate: Date | string | null;
    defaultUnitOfMeasurement: UnitOfMeasurement;
    currency: Currency | null;
    type: ProfileType;
    countryCode: keyof typeof COUNTRY_CODES;
    parentProfileId: number | null;
    parentName: string | null;
}
export enum ProjectMemberSource {
    Regular = 0,
    Inherited = 1,
}

export interface ProfileMember {
    userId: number;
    userName: string;
    email: string;
    isAdmin: boolean;
    profileMemberId: number;
}

export interface ProfileInvite {
    id: number;
    email: string;
    phoneNumber: string;
    firstName: string;
    lastName: string;
    isAdministrator: boolean;
    status: InvitationStatus;
    token: string;
}

export interface ProfileInvite2 {
    id: number;
    profile: IdNamePair;
    inviteeEmail: string;
    inviteeName: string;
    invitedByUser: IdNamePair | null;
    inviteDate: Date | string | null;
    acceptDate: Date | string | null;
    isAdministrator: boolean;
    status: InvitationStatus;
}

export enum InvitationStatus {
    Pending = 'Pending',
    Sent = 'Sent',
    Registered = 'Registered',
    Failed = 'Failed',
    Canceled = 'Canceled',
}

export interface BuyerInvite {
    id: number;
    inviteeEmail: string;
    inviteeName: string;
    inviteePhoneNumber: string | null;
    inviteDate: Date | string | null;
    acceptDate: Date | string | null;
    status: InvitationStatus;
    inviterProfile: IdNamePair | null;
    inviterUser: IdNamePair | null;
    residentialUnitContract: ResidentialUnitContractInfo | null;
}

export interface ResidentialUnitContractInfo {
    id: number;
    unitName: string;
    projectName: string;
    projectId: number;
    unitId: number;
}

export interface ProjectUnit {
    id: number;
    name: string;
    buyers: UnitBuyer[];
    sellerModule: BygrModule;
    buyerModule: BygrModule;
}
export interface UnitBuyer {
    id: number;
    name: string;
    email: string;
    phoneNumber: string;
    lastVisit: Date | string | null;
}

export interface BygrResidentialUnit {
    id: number;
    name: string;
    moveInDate: Date | string | null;
    projectId: number;
    type: number;
    residentialUnitGroupId: number | null;
    isDeleted: boolean;
    internalDoorCount: number | null;
    externalDoorCount: number | null;
    floor: string;
    layoutType: string;
    buyerModules: BygrModule;
    sellerModules: BygrModule;
    selectedMainOptionPresetId: number | null;
    buyers: BygrBuyer[];
}

export interface BygrBuyer {
    applicationUserId: number | null;
    firstName: string;
    lastName: string;
    email: string;
    iconColor: string;
    phoneNumber: string;
    languageCode: string;
    termsAccepted: boolean;
    invitationStatus: InvitationStatus;
    lastVisit: Date | string | null;
    token: string;
}

export interface UpdateUserResponse {
    ok: boolean;
    errorMessage: string;
}

export enum UnitOfMeasurement {
    Millimeter = 'Millimeter',
    Centimeter = 'Centimeter',
}

export interface Currency {
    id: number;
    name: string;
    code: string;
}
export interface ProjectLicenseIssues {
    projectId: number;
    projectName: string;
    sellerProfileId: number;
    sellerProfileName: string;
    projectCreateDate: Date;
    licenseIssues: LicenseStatus[];
}

export type SingleBygrModule = keyof typeof BygrModule;

export interface LicenseStatus {
    module: SingleBygrModule;
    allowedUnits: number;
    actualUnits: number;
    isUnlimited: boolean;
    exceededLimit: boolean;
    note: string;
}

export interface ProjectLicenseDto {
    module: SingleBygrModule;
    allowedUnits: number | null;
    note: string;
}

export interface CreateSubProjectDto {
    parentProjectId: number;
    sellerProfileId: number;
    members: number[];
}

export interface RubenProfileLicenseDto {
    id: number;
    profileId: number;
    profileName: string;
    projectLicenses: RubenProjectLicenseDto[];
    items: RubenProfileLicenseItemDto[];
}

export interface RubenProfileLicenseItemDto {
    profileLicenseId: number;
    isActive: boolean;
    type: RubenProfileLicenseItemType;
}

export enum RubenProfileLicenseItemType {
    ProjectAnalysis = 1001,
    CanViewRubenCaseData = 2002,
    CanViewChatBot = 3001,
}

export interface RubenProjectLicenseDto {
    id: number;
    projectId: number;
    projectName: string;
    profileLicenseId: number;
    items: RubenProjectLicenseItemDto[];
}

export interface RubenProjectLicenseRenderedDto extends RubenProjectLicenseDto {
    hasChatAccess: boolean;
    hasCaseInboxAccess: boolean;
    hasCaseAnalysisAccess: boolean;
}

export interface RubenProjectLicenseItemDto {
    projectLicenseId: number;
    type: RubenProjectLicenseItemType;
    isActive: boolean;
}

export enum RubenProjectLicenseItemType {
    CaseAnalysis = 2001,
    CaseEmail = 2002,
    RubenChat = 2003,
}

export interface ProfileFeatureDTO {
    profileId: number;
    feature: string;
    toggled: boolean;
}

export enum ProfileFeaturesEnum {
    PowerBI = 'PowerBI',
    IsPaying = 'IsPaying',
    CopyProjects = 'CopyProjects',
    Bokamera = 'Bokamera',
    InspectionsInvitation = 'InspectionsInvitation',
    Dependencies = 'Dependencies',
    BirdsEyeView = 'BirdsEyeView',
    ProjectMirroring = 'ProjectMirroring',
}

export interface ElectricitySupplierOffer {
    id: number;
    title: string;
    logoId: number;
    logoFile: FileData | null;
    color: string;
    supplier: ElectricitySupplier;
    description: string;
    externalId: string;
    isCompanyOffer: boolean;
    sortOrder: number;
    termSheetLink1: string;
    termSheetLink2: string;
    termSheetLink3: string;
    terms: string;
    priceListLink: string;
    highlightedTitle1: string;
    highlightedSubTitle1: string;
    highlightedTitle2: string;
    highlightedSubTitle2: string;
    highlightedTitle3: string;
    highlightedSubTitle3: string;
    protocolAdditionalInfo: string;
}

export enum ElectricitySupplier {
    Fjordkraft = 'Fjordkraft', // 1
    Fortum = 'Fortum', // 2
    TrondelagKraft = 'Trøndelag Kraft', // 3
}
